if (!customElements.get('multi-pedal-builder')) {
  // Last step before the customer submits. On the stacked (mobile/tablet) layout
  // the sidebar sits on top of the steps, so it is collapsed until this step;
  // on desktop it stays visible throughout — see the modifier class in the CSS.
  var MPB_REVIEW_STEP = 5;

  class MultiPedalBuilder extends HTMLElement {
    connectedCallback() {
      console.log('[multi-pedal-builder] init v4 (captcha-aware submit)');
      // Prefer our own <form>; fall back to an ancestor form in case the markup
      // is nested inside another form (browsers drop nested <form> tags).
      this.form = this.querySelector('form') || this.closest('form');

      // Submission is 100% JS-driven. The submit button is type="button", so it
      // can never trigger a native (page-reloading) submit — the only way to
      // send the form is through submitForm(). Wire the button click up front,
      // before anything that could throw, so this path is always live.
      var submitBtn = this.querySelector('[data-submit]');
      if (submitBtn) submitBtn.addEventListener('click', (e) => this.submitForm(e));

      // Safety net: also intercept native submit (e.g. Enter key in a field) and
      // disable native validation so we control the flow entirely.
      if (this.form) {
        this.form.setAttribute('novalidate', '');
        this.form.addEventListener('submit', (e) => this.submitForm(e));
      }

      // Must run before anything can focus a field inside the form — see setupCaptcha().
      this.captchaState = 'off';   // 'off' | 'pending' | 'ready'
      this.setupCaptcha();

      // Parse config defensively: a malformed/edited setting must not take the
      // whole builder down.
      try {
        var configEl = this.querySelector('script[data-mpb-config]');
        this.config = configEl ? JSON.parse(configEl.textContent) : {};
      } catch (err) {
        console.error('Multi-pedal builder: could not parse config', err);
        this.config = {};
      }

      this.currentStep = 1;

      // State
      this.pedalType = null;        // 'guitar' | 'bass'
      this.selectedPedals = [];     // [{ title, type, image }]
      this.selectedExtras = [];     // ['extra name', ...]
      this.signalChain = 'Leave it with us';
      this.artworkType = null;      // 'gallery' | 'custom'
      this.selectedArtwork = null;  // artwork title or null
      this.customVision = '';
      this.customReference = '';

      // Price tiers from section settings
      this.priceTiers = {
        tier1: this.config.priceTier1,
        tier2: this.config.priceTier2,
        tier3: this.config.priceTier3
      };

      // Step labels
      this.stepLabels = {
        1: 'Choose your instrument type',
        2: 'Select your pedals',
        3: 'Extras and signal chain',
        4: 'Choose your artwork',
        5: 'Preferences and contact details'
      };

      this.buildLightbox();
      this.bindEvents();

      // Fallback path: if the POST ever goes out as a real page-reloading submit
      // (Shopify's wrapper navigating instead of calling sendForm), the reloaded
      // page carries form.posted_successfully? and we land straight on success.
      var postedMarker = this.querySelector('[data-mpb-posted]');
      var serverError = this.querySelector('[data-mpb-error]');
      if (postedMarker || (serverError && !serverError.hidden)) {
        // Shopify reports form errors on the last step, so open there instead of
        // leaving the message behind a hidden step.
        if (postedMarker) this.showSuccess(); else this.showStep(5);
        this.scrollIntoView({ block: 'start' });
      }
    }

    bindEvents() {
      // Step navigation
      this.querySelectorAll('[data-next]').forEach(btn =>
        btn.addEventListener('click', () => this.nextStep())
      );
      this.querySelectorAll('[data-prev]').forEach(btn =>
        btn.addEventListener('click', () => this.prevStep())
      );

      // Step 1: Instrument type
      this.querySelectorAll('[data-type]').forEach(btn =>
        btn.addEventListener('click', () => this.selectType(btn))
      );

      // Step 2: Pedal selection
      this.querySelectorAll('[data-pedal-card]').forEach(card =>
        card.addEventListener('click', () => this.togglePedal(card))
      );

      // Step 3: Extras
      this.querySelectorAll('[data-extra]').forEach(cb =>
        cb.addEventListener('change', () => this.updateExtras())
      );

      // Step 3: Signal chain
      var signalSelect = this.querySelector('[data-signal-chain]');
      if (signalSelect) {
        signalSelect.addEventListener('change', () => {
          this.signalChain = signalSelect.value;
        });
      }

      // Step 4: Artwork path choice
      this.querySelectorAll('[data-artwork-path]').forEach(btn =>
        btn.addEventListener('click', () => this.selectArtworkPath(btn.dataset.artworkPath))
      );

      // Step 4: Artwork back
      this.querySelectorAll('[data-artwork-back]').forEach(btn =>
        btn.addEventListener('click', () => this.showArtworkChoice())
      );

      // Step 4: Artwork filters
      this.querySelectorAll('[data-aw-filter]').forEach(btn =>
        btn.addEventListener('click', () => this.filterArtworks(btn))
      );

      // Step 4: Artwork card selection
      this.querySelectorAll('[data-aw-card]').forEach(card =>
        card.addEventListener('click', () => this.selectArtwork(card))
      );

      // Step 4: Custom vision textarea — update Next button on input
      var visionTextarea = this.querySelector('[data-custom-vision]');
      if (visionTextarea) {
        visionTextarea.addEventListener('input', () => this.updateNextButton());
      }

      // Step 4: Artwork zoom (magnifier → lightbox). Sits inside the card
      // button, so stop propagation to avoid selecting the card when zooming.
      this.querySelectorAll('[data-zoom]').forEach(el => {
        el.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.openLightbox(el.dataset.zoomSrc, el.dataset.zoomTitle);
        });
        el.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            e.stopPropagation();
            this.openLightbox(el.dataset.zoomSrc, el.dataset.zoomTitle);
          }
        });
      });

      // Form submit is bound in connectedCallback (before config parsing) so the
      // form can never fall back to a native, page-reloading submit.

      // Restart
      var restartBtn = this.querySelector('[data-restart]');
      if (restartBtn) {
        restartBtn.addEventListener('click', () => this.restart());
      }
    }

    // ---- Step navigation ----

    showStep(step) {
      this.currentStep = step;

      var sidebar = this.querySelector('[data-sidebar]');
      if (sidebar) {
        sidebar.classList.toggle(
          'mpb__sidebar--collapsed-until-review',
          step !== MPB_REVIEW_STEP
        );
      }

      // Update step visibility
      this.querySelectorAll('[data-step]').forEach(el => {
        el.classList.toggle('mpb__step--active', el.dataset.step === String(step));
      });

      // Update step indicator dots and lines
      this.querySelectorAll('[data-step-dot]').forEach(dot => {
        var dotStep = parseInt(dot.dataset.stepDot);
        dot.classList.toggle('mpb__step-dot--active', dotStep === step);
        dot.classList.toggle('mpb__step-dot--completed', dotStep < step);
      });
      this.querySelectorAll('[data-step-line]').forEach(line => {
        var lineStep = parseInt(line.dataset.stepLine);
        line.classList.toggle('mpb__step-line--active', lineStep < step);
      });

      // Update step label
      var label = this.querySelector('[data-step-label]');
      if (label && this.stepLabels[step]) {
        label.textContent = this.stepLabels[step];
      } else if (label) {
        label.textContent = '';
      }

      // Scroll to top
      window.scrollTo({ top: this.offsetTop - 100, behavior: 'smooth' });
    }

    nextStep() {
      if (!this.validateCurrentStep()) return;
      this.showStep(this.currentStep + 1);
    }

    prevStep() {
      if (this.currentStep > 1) {
        this.showStep(this.currentStep - 1);
      }
    }

    updateNextButton() {
      var stepEl = this.querySelector('[data-step="' + this.currentStep + '"]');
      if (!stepEl) return;
      var nextBtn = stepEl.querySelector('[data-next]');
      if (!nextBtn) return;

      var valid = this.validateCurrentStep();
      nextBtn.disabled = !valid;
    }

    // ---- Step 1: Instrument type ----

    selectType(btn) {
      this.pedalType = btn.dataset.type;

      // Update card styles
      this.querySelectorAll('[data-type]').forEach(c =>
        c.classList.toggle('mpb__type-card--selected', c === btn)
      );

      // Show correct pedal grid and reset selection
      this.querySelectorAll('[data-pedals-grid]').forEach(grid => {
        grid.style.display = grid.dataset.pedalsGrid === this.pedalType ? '' : 'none';
      });
      this.selectedPedals = [];
      this.querySelectorAll('[data-pedal-card]').forEach(c => {
        c.classList.remove('mpb__pedal-card--selected');
        var badge = c.querySelector('[data-pedal-badge]');
        if (badge) badge.textContent = '';
      });

      this.updateNextButton();
      this.updateSidebar();
    }

    // ---- Step 2: Pedals ----

    togglePedal(card) {
      // Only act on cards in the visible grid
      var grid = card.closest('[data-pedals-grid]');
      if (grid && grid.style.display === 'none') return;

      var title = card.dataset.pedalTitle;
      var idx = this.selectedPedals.findIndex(p => p.title === title);

      if (idx > -1) {
        // Deselect
        this.selectedPedals.splice(idx, 1);
        card.classList.remove('mpb__pedal-card--selected');
      } else {
        // Select
        this.selectedPedals.push({
          title: title,
          type: card.dataset.pedalType,
          image: card.dataset.pedalImage
        });
        card.classList.add('mpb__pedal-card--selected');
      }

      // Re-number badges
      this.renumberBadges();
      this.updateNextButton();
      this.updateSidebar();
    }

    renumberBadges() {
      var visibleGrid = this.querySelector('[data-pedals-grid="' + this.pedalType + '"]');
      if (!visibleGrid) return;

      visibleGrid.querySelectorAll('[data-pedal-card]').forEach(card => {
        var badge = card.querySelector('[data-pedal-badge]');
        if (!badge) return;
        var title = card.dataset.pedalTitle;
        var idx = this.selectedPedals.findIndex(p => p.title === title);
        if (idx > -1) {
          badge.textContent = idx + 1;
        } else {
          badge.textContent = '';
        }
      });
    }

    // ---- Step 3: Extras ----

    updateExtras() {
      this.selectedExtras = [];
      this.querySelectorAll('[data-extra]:checked').forEach(cb => {
        this.selectedExtras.push(cb.value);
      });
      this.updateSidebar();
    }

    // ---- Step 4: Artwork ----

    selectArtworkPath(path) {
      this.artworkType = path;

      // Update choice card styles
      this.querySelectorAll('[data-artwork-path]').forEach(c =>
        c.classList.toggle('mpb__type-card--selected', c.dataset.artworkPath === path)
      );

      // Show the relevant sub-screen, hide choice
      this.querySelector('[data-artwork-choice]').style.display = 'none';
      this.querySelectorAll('[data-artwork-screen]').forEach(el => {
        el.style.display = el.dataset.artworkScreen === path ? '' : 'none';
      });

      // Reset artwork selection when switching paths
      if (path === 'gallery') {
        this.customVision = '';
        this.customReference = '';
      } else {
        this.selectedArtwork = null;
        this.querySelectorAll('[data-aw-card]').forEach(c =>
          c.classList.remove('mpb__artwork-card--selected')
        );
      }

      this.updateNextButton();
      this.updateSidebar();
    }

    showArtworkChoice() {
      this.artworkType = null;
      this.selectedArtwork = null;
      this.customVision = '';
      this.customReference = '';

      this.querySelector('[data-artwork-choice]').style.display = '';
      this.querySelectorAll('[data-artwork-screen]').forEach(el => {
        el.style.display = 'none';
      });
      this.querySelectorAll('[data-artwork-path]').forEach(c =>
        c.classList.remove('mpb__type-card--selected')
      );
      this.querySelectorAll('[data-aw-card]').forEach(c =>
        c.classList.remove('mpb__artwork-card--selected')
      );

      this.updateNextButton();
      this.updateSidebar();
    }

    filterArtworks(activeBtn) {
      var filter = activeBtn.dataset.awFilter;

      this.querySelectorAll('[data-aw-filter]').forEach(btn =>
        btn.classList.toggle('mpb__filter--active', btn === activeBtn)
      );

      this.querySelectorAll('[data-aw-card]').forEach(card => {
        card.style.display = (filter === 'all' || card.dataset.awSeries === filter) ? '' : 'none';
      });
    }

    selectArtwork(card) {
      this.querySelectorAll('[data-aw-card]').forEach(c =>
        c.classList.remove('mpb__artwork-card--selected')
      );
      card.classList.add('mpb__artwork-card--selected');
      this.selectedArtwork = card.dataset.awTitle;

      this.updateNextButton();
      this.updateSidebar();
    }

    // ---- Image lightbox ----

    buildLightbox() {
      var lb = document.createElement('div');
      lb.className = 'mpb__lightbox';
      lb.setAttribute('hidden', '');
      lb.setAttribute('role', 'dialog');
      lb.setAttribute('aria-modal', 'true');
      lb.innerHTML =
        '<button type="button" class="mpb__lightbox-close" data-lightbox-close aria-label="Close">' +
          '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>' +
        '</button>' +
        '<figure class="mpb__lightbox-fig">' +
          '<img class="mpb__lightbox-img" data-lightbox-img src="" alt="">' +
          '<figcaption class="mpb__lightbox-caption" data-lightbox-caption></figcaption>' +
        '</figure>';
      document.body.appendChild(lb);
      this.lightbox = lb;

      lb.addEventListener('click', (e) => {
        if (e.target === lb || e.target.closest('[data-lightbox-close]')) {
          this.closeLightbox();
        }
      });
      this._lightboxKeydown = (e) => {
        if (e.key === 'Escape') this.closeLightbox();
      };
    }

    openLightbox(src, title) {
      if (!this.lightbox || !src) return;
      var img = this.lightbox.querySelector('[data-lightbox-img]');
      var cap = this.lightbox.querySelector('[data-lightbox-caption]');
      img.src = src;
      img.alt = title || '';
      if (cap) {
        cap.textContent = title || '';
        cap.style.display = title ? '' : 'none';
      }
      this.lightbox.removeAttribute('hidden');
      document.body.classList.add('mpb-lightbox-open');
      document.addEventListener('keydown', this._lightboxKeydown);
      var closeBtn = this.lightbox.querySelector('[data-lightbox-close]');
      if (closeBtn) closeBtn.focus();
    }

    closeLightbox() {
      if (!this.lightbox) return;
      this.lightbox.setAttribute('hidden', '');
      document.body.classList.remove('mpb-lightbox-open');
      document.removeEventListener('keydown', this._lightboxKeydown);
      var img = this.lightbox.querySelector('[data-lightbox-img]');
      if (img) img.src = '';
    }

    // ---- Step 4 validation: custom path needs vision filled ----

    validateCurrentStep() {
      switch (this.currentStep) {
        case 1:
          return this.pedalType !== null;
        case 2:
          return this.selectedPedals.length >= 2;
        case 4:
          if (this.artworkType === 'gallery') return this.selectedArtwork !== null;
          if (this.artworkType === 'custom') {
            var vision = this.querySelector('[data-custom-vision]');
            return vision && vision.value.trim().length > 0;
          }
          return false;
        default:
          return true;
      }
    }

    // ---- Sidebar ----

    updateSidebar() {
      // Type
      var typeRow = this.querySelector('[data-sidebar-type]');
      if (this.pedalType) {
        typeRow.style.display = '';
        this.querySelector('[data-sidebar-type-value]').textContent =
          this.pedalType.charAt(0).toUpperCase() + this.pedalType.slice(1) + ' Multi-Pedal';
      } else {
        typeRow.style.display = 'none';
      }

      // Pedals
      var pedalsRow = this.querySelector('[data-sidebar-pedals]');
      var pedalsList = this.querySelector('[data-sidebar-pedals-list]');
      if (this.selectedPedals.length > 0) {
        pedalsRow.style.display = '';
        pedalsList.innerHTML = this.selectedPedals.map(function(p, i) {
          return '<div class="mpb__sidebar-pedal-item">' +
            '<span class="mpb__sidebar-pedal-num">' + (i + 1) + '</span>' +
            '<div class="mpb__sidebar-pedal-thumb"><img src="' + p.image + '" alt="' + p.title + '" width="32" height="32"></div>' +
            '<div class="mpb__sidebar-pedal-info">' +
              '<span class="mpb__sidebar-pedal-name">' + p.title + '</span>' +
              '<span class="mpb__sidebar-pedal-category">' + p.type + '</span>' +
            '</div>' +
          '</div>';
        }).join('');
      } else {
        pedalsRow.style.display = 'none';
        pedalsList.innerHTML = '';
      }

      // Extras
      var extrasRow = this.querySelector('[data-sidebar-extras]');
      var extrasList = this.querySelector('[data-sidebar-extras-list]');
      if (this.selectedExtras.length > 0) {
        extrasRow.style.display = '';
        extrasList.innerHTML = this.selectedExtras.map(function(e) {
          return '<li>' + e + '</li>';
        }).join('');
      } else {
        extrasRow.style.display = 'none';
        extrasList.innerHTML = '';
      }

      // Artwork
      var artworkRow = this.querySelector('[data-sidebar-artwork]');
      if (this.artworkType === 'gallery' && this.selectedArtwork) {
        artworkRow.style.display = '';
        this.querySelector('[data-sidebar-artwork-value]').textContent = this.selectedArtwork;
      } else if (this.artworkType === 'custom') {
        artworkRow.style.display = '';
        this.querySelector('[data-sidebar-artwork-value]').textContent = 'Custom design';
      } else {
        artworkRow.style.display = 'none';
      }

      // Cost estimate
      var costSection = this.querySelector('[data-sidebar-cost]');
      var timelineSection = this.querySelector('[data-sidebar-timeline]');
      if (this.selectedPedals.length >= 2) {
        costSection.style.display = '';
        timelineSection.style.display = '';

        var count = this.selectedPedals.length;
        var tierText, noteText;
        if (count <= 6) {
          tierText = this.priceTiers.tier1;
        } else if (count <= 12) {
          tierText = this.priceTiers.tier2;
        } else {
          tierText = this.priceTiers.tier3;
        }
        noteText = 'Based on ' + count + ' pedals. Final pricing depends on pedal selection and extras.';

        this.querySelector('[data-sidebar-tier]').textContent = tierText;
        this.querySelector('[data-sidebar-cost-note]').textContent = noteText;
      } else {
        costSection.style.display = 'none';
        timelineSection.style.display = 'none';
      }
    }

    // ---- Form submission ----

    async submitForm(event) {
      event.preventDefault();
      if (this.isSubmitting) return;

      // Validate step 5 required fields. Bring the first invalid field into view
      // and focus it before reporting — the native validation bubble alone can
      // land off-screen on this long step and read as "nothing happened".
      var step5 = this.querySelector('[data-step="5"]');
      var requiredFields = step5 ? step5.querySelectorAll('[required]') : [];
      for (var field of requiredFields) {
        if (!field.checkValidity()) {
          field.scrollIntoView({ block: 'center', behavior: 'smooth' });
          field.focus({ preventScroll: true });
          field.reportValidity();
          return;
        }
      }

      // Populate hidden fields
      this.querySelector('[data-field-pedal-type]').value = this.pedalType;
      this.querySelector('[data-field-selected-pedals]').value =
        this.selectedPedals.map(function(p) { return p.title; }).join(', ');
      this.querySelector('[data-field-extras]').value = this.selectedExtras.join(', ');
      this.querySelector('[data-field-signal-chain]').value = this.signalChain;
      this.querySelector('[data-field-artwork-type]').value = this.artworkType;

      if (this.artworkType === 'gallery' && this.selectedArtwork) {
        this.querySelector('[data-field-selected-artwork]').value = this.selectedArtwork;
      }
      if (this.artworkType === 'custom') {
        var visionEl = this.querySelector('[data-custom-vision]');
        var refEl = this.querySelector('[data-custom-reference]');
        this.querySelector('[data-field-custom-vision]').value = visionEl ? visionEl.value : '';
        this.querySelector('[data-field-custom-reference]').value = refEl ? refEl.value : '';
      }

      if (!this.form) {
        console.error('Multi-pedal builder: no form found — cannot submit.');
        return;
      }

      this.isSubmitting = true;
      this.showError('');
      var submitBtn = this.querySelector('[data-submit]');
      submitBtn.disabled = true;
      submitBtn.classList.add('loading');

      // Hand the form to Shopify's CAPTCHA wrapper, which calls sendForm() once
      // the token is in place. If hCaptcha is still loading, wait for the ready
      // callback instead of firing a tokenless POST.
      if (this.captchaState === 'pending') {
        this.waitingForCaptcha = true;
      } else {
        this.form.submit();
      }

      // The wrapper stays silent when the token request fails, so don't leave
      // the button spinning forever.
      clearTimeout(this.submitWatchdog);
      this.submitWatchdog = setTimeout(
        () => this.failSubmit(new Error('CAPTCHA/submit timed out')),
        20000
      );
    }

    // Shopify's storefront CAPTCHA script (injected through content_for_header)
    // takes over every form that posts to /contact: when it binds the form it
    // captures the form's current submit function, then replaces form.submit
    // with its own, which fetches an hCaptcha token, appends it to the form as
    // the h-captcha-response input and finally calls the captured function.
    // A bare fetch() of the form skips that wrapper entirely, which is why
    // /contact answered 400 "Missing CAPTCHA token". Installing our AJAX sender
    // as form.submit *before* the wrapper binds (it binds on the first focusin
    // inside the form, so this has to happen during init) is what makes the
    // wrapper hand the token-carrying form back to us instead of navigating.
    setupCaptcha() {
      if (!this.form) return;

      // Captured by the wrapper on bind; also the direct sender on shops that
      // have CAPTCHA switched off, where nothing ever replaces form.submit.
      this.form.submit = () => this.sendForm();

      var captcha = window.Shopify && window.Shopify.captcha;
      if (!captcha || typeof captcha.protect !== 'function') return;

      // protect() binds the form up front and calls back once hCaptcha is ready.
      this.captchaState = 'pending';
      try {
        captcha.protect(this.form, () => {
          this.captchaState = 'ready';
          if (this.waitingForCaptcha) {
            this.waitingForCaptcha = false;
            this.form.submit();
          }
        });
      } catch (err) {
        console.error('Multi-pedal builder: could not wire up CAPTCHA', err);
        this.captchaState = 'off';
      }
    }

    // Called by the CAPTCHA wrapper (see setupCaptcha) with the token already in
    // the form, or directly when the shop has no CAPTCHA.
    async sendForm() {
      if (!this.form || this.sendInFlight) return;
      this.sendInFlight = true;

      try {
        var response = await fetch(this.form.action, {
          method: 'POST',
          body: new FormData(this.form),
          redirect: 'manual'
        });

        // An accepted contact post answers with a redirect back to the page,
        // which `redirect: 'manual'` surfaces as an opaque response (status 0).
        // Everything else — 400 missing/invalid token, 422, 429 — is a real
        // failure and must not be dressed up as a success.
        if (!response.ok && response.type !== 'opaqueredirect' && response.status !== 0) {
          throw new Error('Contact form responded ' + response.status);
        }

        this.showSuccess();
      } catch (error) {
        this.failSubmit(error);
      } finally {
        this.sendInFlight = false;
      }
    }

    showSuccess() {
      clearTimeout(this.submitWatchdog);
      this.isSubmitting = true;
      this.showError('');

      var submitBtn = this.querySelector('[data-submit]');
      if (submitBtn) submitBtn.classList.remove('loading');

      // Build success message. Skipped after a page-reloading fallback submit,
      // where the build state is gone and the markup's generic copy is right.
      if (this.pedalType) {
        var msg = 'Our team will review your ' + this.pedalType + ' multi-pedal build (' +
          this.selectedPedals.length + ' pedals) and get back to you with a quote.';
        var msgEl = this.querySelector('[data-success-msg]');
        if (msgEl) msgEl.textContent = msg;
      }

      // Hide sidebar and show success
      var sidebar = this.querySelector('[data-sidebar]');
      if (sidebar) sidebar.style.display = 'none';

      // Hide step indicator
      var indicator = this.querySelector('.mpb__steps-indicator');
      if (indicator) indicator.style.display = 'none';
      var stepLabel = this.querySelector('[data-step-label]');
      if (stepLabel) stepLabel.style.display = 'none';

      this.showStep('success');
    }

    failSubmit(error) {
      clearTimeout(this.submitWatchdog);
      if (!this.isSubmitting) return;

      console.error('Form submission error:', error);
      this.isSubmitting = false;
      this.waitingForCaptcha = false;

      var submitBtn = this.querySelector('[data-submit]');
      if (submitBtn) {
        submitBtn.disabled = false;
        submitBtn.classList.remove('loading');
      }

      this.showError(
        "We couldn't send your request. Please try again — if it keeps failing, email us and we'll pick it up from there."
      );
    }

    showError(message) {
      var el = this.querySelector('[data-mpb-error]');
      if (!el) return;
      el.textContent = message || '';
      el.hidden = !message;
    }

    // ---- Restart ----

    restart() {
      // Reset state
      clearTimeout(this.submitWatchdog);
      this.isSubmitting = false;
      this.sendInFlight = false;
      this.waitingForCaptcha = false;
      this.showError('');
      this.pedalType = null;
      this.selectedPedals = [];
      this.selectedExtras = [];
      this.signalChain = 'Leave it with us';
      this.artworkType = null;
      this.selectedArtwork = null;
      this.customVision = '';
      this.customReference = '';

      // Reset form
      this.form.reset();

      // Reset UI: type cards
      this.querySelectorAll('[data-type]').forEach(c => c.classList.remove('mpb__type-card--selected'));

      // Reset pedal grids
      this.querySelectorAll('[data-pedals-grid]').forEach(g => g.style.display = 'none');
      this.querySelectorAll('[data-pedal-card]').forEach(c => {
        c.classList.remove('mpb__pedal-card--selected');
        var badge = c.querySelector('[data-pedal-badge]');
        if (badge) badge.textContent = '';
      });

      // Reset extras
      this.querySelectorAll('[data-extra]').forEach(cb => cb.checked = false);

      // Reset artwork
      this.showArtworkChoice();

      // Restore sidebar and indicators
      var sidebar = this.querySelector('[data-sidebar]');
      if (sidebar) sidebar.style.display = '';
      var indicator = this.querySelector('.mpb__steps-indicator');
      if (indicator) indicator.style.display = '';
      var stepLabel = this.querySelector('[data-step-label]');
      if (stepLabel) stepLabel.style.display = '';

      // Re-enable submit
      var submitBtn = this.querySelector('[data-submit]');
      if (submitBtn) {
        submitBtn.disabled = false;
        submitBtn.classList.remove('loading');
      }

      this.updateSidebar();
      this.showStep(1);
    }
  }

  customElements.define('multi-pedal-builder', MultiPedalBuilder);
}
